/**
 * trim-timeline.js — a premium inline video-trim timeline.
 *
 * Built for the screen-recorder's "trim right here" flow (feedback
 * together-tima 2026-07-29: «добавьте … чтобы можно было прямо тут обрезать
 * видео!»), designed to be the reference example: a filmstrip of REAL frames
 * from the clip, a bright keep-region between two big grip handles, dimmed
 * cut-regions, a scrubbing playhead, live time chips, set-in/out at the
 * playhead, and full keyboard control. Theme-aware, responsive, accessible.
 *
 * It is UI only — playback is driven by the caller's <video>/player (via
 * onScrub) and the lossless export is the caller's job (MP4BoxTrim / FFmpeg
 * -c copy). This keeps the component reusable by any tool with a video and a
 * range to cut.
 *
 *   var tt = window.createTrimTimeline(containerEl, {
 *       onScrub:  function(t){ player.seek(t); },      // playhead moved
 *       onChange: function(s,e){ ... },                // keep-range changed
 *       labels: { setIn, setOut, keep, start, end },   // localized strings
 *   });
 *   tt.load(fileOrUrl, durationSec);   // builds filmstrip
 *   tt.setPlayhead(video.currentTime); // reflect playback (on timeupdate)
 *   tt.getRange();                     // {start, end}
 *   tt.setRange(s, e);
 *   tt.destroy();
 */
(function () {
    'use strict';
    // window.__perPixel / window.__setText (timbrica-core.js) write a position or a text only when what is
    // shown changes. A page built before they existed can still be handed this file — it then writes the
    // plain way. Why: docs/why/primitives/per-pixel-writes.md.
    function pxWrite(e, p, v, u) { if (window.__perPixel) return window.__perPixel(e, p, v, u); e.style[p] = v + (u || '%'); }

    if (window.createTrimTimeline) return;

    var cssInjected = false;
    var CSS = `
.tt{--tt-accent:var(--accent,#38bdf8);width:100%;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;-webkit-user-select:none;user-select:none}
.tt-strip{position:relative;height:64px;border-radius:12px;overflow:hidden;background:var(--bg-tertiary,#0d1017);box-shadow:inset 0 0 0 1px var(--border,rgba(255,255,255,.1));touch-action:none;cursor:pointer}
.tt-cells{position:absolute;inset:0;display:flex}
.tt-cell{flex:1 1 0;min-width:0;background-size:cover;background-position:center;background-repeat:no-repeat;background-color:var(--bg-tertiary,#0d1017);border-right:1px solid rgba(0,0,0,.25)}
.tt-cell:last-child{border-right:none}
.tt-cell.tt-shimmer{background-image:linear-gradient(100deg,transparent 20%,rgba(255,255,255,.06) 50%,transparent 80%);background-size:200% 100%;animation:tt-shim 1.1s linear infinite}
@keyframes tt-shim{from{background-position:180% 0}to{background-position:-80% 0}}
/* dim the cut-away parts — desaturated + darkened, not alarming red: cutting is a choice, not an error */
.tt-dim{position:absolute;top:0;bottom:0;background:rgba(6,8,13,.62);backdrop-filter:grayscale(.7) brightness(.7);-webkit-backdrop-filter:grayscale(.7) brightness(.7);z-index:1;pointer-events:none}
.tt-dim-l{left:0}
.tt-dim-r{right:0}
/* the kept region — bright frame, accent rails on ALL FOUR sides: the two
   vertical rails ARE the cut edges, and they must read as edges even when a
   handle sits over them. */
.tt-keep{position:absolute;top:0;bottom:0;z-index:2;pointer-events:none;
    box-shadow:inset 0 2px 0 var(--tt-accent),inset 0 -2px 0 var(--tt-accent),
               inset 2px 0 0 var(--tt-accent),inset -2px 0 0 var(--tt-accent)}
/* ── grip handles ─────────────────────────────────────────────────────────
   ⚠️⚠️ Ручки ОБЯЗАНЫ лежать ВНУТРИ полосы. До 2026-09-08 старт нёс
   transform:translateX(-100%), конец стоял на left:w, а у полосы стоит
   overflow:hidden — и в исходном состоянии (0..dur, то есть у КАЖДОГО, кто
   только что записал) обе ручки были срезаны подчистую. Замер на живой
   странице: visibleW=0 у обеих, elementFromPoint в центре ручки → null,
   то есть их нельзя было ни увидеть, ни схватить. Обрезка существовала
   только через кнопки «Отметить начало/конец», и те при плейхеде в нуле
   резали дубль до 0.2 с. Позиция считается в render() с зажимом в [0, w-hw]:
   на краю ручка ложится ПОВЕРХ первых/последних пикселей оставляемого куска
   (так делают все видеоредакторы), в середине — снаружи от него. */
.tt-handle{position:absolute;top:0;bottom:0;width:16px;z-index:4;cursor:ew-resize;display:flex;align-items:center;justify-content:center;background:var(--tt-accent);touch-action:none;box-shadow:0 0 0 1px rgba(0,0,0,.35)}
.tt-handle-s{border-radius:8px 0 0 8px}
.tt-handle-e{border-radius:0 8px 8px 0}
.tt-handle::after{content:'';width:3px;height:22px;border-radius:2px;background:rgba(255,255,255,.92);
    box-shadow:-4px 0 0 rgba(255,255,255,.92),4px 0 0 rgba(255,255,255,.92)}

.tt-handle:hover,.tt-handle:focus-visible,.tt-handle.tt-drag{filter:brightness(1.15);box-shadow:0 0 0 1px rgba(0,0,0,.35),0 0 0 3px color-mix(in srgb,var(--tt-accent) 35%,transparent)}
.tt-handle:focus{outline:none}
.tt-handle:focus-visible{outline:2px solid #fff;outline-offset:1px}
/* keyframe ticks — the points a lossless cut can start on */
.tt-kf-layer{position:absolute;inset:0;z-index:3;pointer-events:none}
.tt-kf{position:absolute;top:0;bottom:0;width:1px;background:rgba(255,255,255,.18)}
/* playhead */
.tt-playhead{position:absolute;top:-4px;bottom:-4px;width:2px;background:#fff;z-index:5;pointer-events:none;box-shadow:0 0 6px rgba(0,0,0,.6);transform:translateX(-1px)}
.tt-playhead::before{content:'';position:absolute;top:-5px;left:50%;width:11px;height:11px;border-radius:50%;background:#fff;transform:translateX(-50%);box-shadow:0 1px 4px rgba(0,0,0,.5)}
/* readout row */
.tt-readout{display:flex;align-items:center;gap:10px;margin-top:12px;flex-wrap:wrap}
.tt-side{display:flex;align-items:center;gap:7px}
.tt-chip{font-variant-numeric:tabular-nums;font-size:13px;color:var(--text-secondary,#9aa4b2);white-space:nowrap;display:flex;flex-direction:column;line-height:1.25}
.tt-chip b{font-size:15px;color:var(--text-primary,#fff);font-weight:600}
.tt-chip .tt-chip-k{font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:var(--text-muted,#6b7280)}
.tt-keepchip{margin:0 auto;text-align:center;padding:5px 16px;border-radius:999px;background:color-mix(in srgb,var(--tt-accent) 16%,transparent);border:1px solid color-mix(in srgb,var(--tt-accent) 45%,transparent)}
.tt-keepchip b{color:var(--tt-accent)}
.tt-setbtn{border:1px solid var(--border,rgba(255,255,255,.14));background:var(--glass,rgba(255,255,255,.05));color:var(--text-secondary,#9aa4b2);border-radius:8px;padding:6px 9px;font-size:12px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;transition:border-color .15s,color .15s,background .15s;white-space:nowrap}
.tt-setbtn:hover:not(:disabled){border-color:var(--tt-accent);color:var(--text-primary,#fff);background:var(--glass-hover,rgba(255,255,255,.08))}
/* Кнопка ставит границу ТУДА, ГДЕ СТОИТ КУРСОР ВОСПРОИЗВЕДЕНИЯ. Пока курсор
   в нуле, «Отметить конец» означало «оставить 0.2 секунды» — и именно это
   происходило: дубль схлопывался, а кнопка скачивания начинала обещать
   «Скачать обрезанное · 0:00». Кнопка, которая может только испортить,
   обязана быть выключена и сказать почему (title). */
.tt-setbtn:disabled{opacity:.42;cursor:not-allowed}
.tt-hint{margin-top:9px;font-size:12px;line-height:1.5;color:var(--text-muted,#6b7280);text-align:center;text-wrap:pretty}
.tt-setbtn kbd{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;background:rgba(127,127,127,.2);border-radius:3px;padding:0 4px;line-height:1.4}
@media(max-width:560px){
    .tt-strip{height:54px}
    .tt-readout{gap:8px}
    .tt-keepchip{order:-1;flex-basis:100%}
    .tt-setbtn kbd{display:none}
}
@media(pointer:coarse){
    .tt-handle{width:22px}
    .tt-handle::after{height:26px}
    /* Палец промахивается мимо 22 px — невидимый ореол ловит промах, как в
       /video-trim. Только на касании: на мыши он съел бы по ~26 px полосы
       под перемотку у каждого края (та же ловушка Viktor #936, с изнанки). */
    .tt-handle::before{content:'';position:absolute;top:0;bottom:0;left:-11px;right:-11px}
}
@media(prefers-reduced-motion:reduce){ .tt-cell.tt-shimmer{animation:none} }
`;

    function injectCSS() {
        if (cssInjected) return;
        var s = document.createElement('style'); s.textContent = CSS;
        document.head.appendChild(s); cssInjected = true;
    }
    function fmt(t) {
        t = Math.max(0, t || 0);
        var m = Math.floor(t / 60), s = t - m * 60;
        var ss = (s < 10 ? '0' : '') + s.toFixed(1);
        return m + ':' + ss;
    }
    function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
    function el(tag, cls) { var e = document.createElement(tag); if (cls) e.className = cls; return e; }

    window.createTrimTimeline = function (container, opts) {
        opts = opts || {};
        injectCSS();
        var labels = opts.labels || {};
        var MIN = 0.2;      // shortest keepable clip when dragging a handle (s)
        var MIN_BTN = 0.5;  // shortest clip a BUTTON press may leave behind (s)

        var dur = 0, startT = 0, endT = 0, playT = 0, destroyed = false;
        var keyframes = [];   // seconds; a lossless copy-cut can only START on one
        function snapStart(t) {
            if (!keyframes.length) return t;
            var best = keyframes[0], bd = Math.abs(t - best);
            for (var i = 1; i < keyframes.length; i++) { var d = Math.abs(t - keyframes[i]); if (d < bd) { bd = d; best = keyframes[i]; } }
            return best;
        }

        var root = el('div', 'tt');
        var strip = el('div', 'tt-strip');
        var cells = el('div', 'tt-cells');
        var dimL = el('div', 'tt-dim tt-dim-l');
        var dimR = el('div', 'tt-dim tt-dim-r');
        var keep = el('div', 'tt-keep');
        var hS = el('div', 'tt-handle tt-handle-s');
        var hE = el('div', 'tt-handle tt-handle-e');
        var playhead = el('div', 'tt-playhead');
        [hS, hE].forEach(function (h, i) {
            h.tabIndex = 0; h.setAttribute('role', 'slider'); h.setAttribute('aria-label', i === 0 ? (labels.start || 'Trim start') : (labels.end || 'Trim end'));
        });
        var kfLayer = el('div', 'tt-kf-layer');
        strip.appendChild(cells); strip.appendChild(kfLayer); strip.appendChild(dimL); strip.appendChild(dimR);
        strip.appendChild(keep); strip.appendChild(hS); strip.appendChild(hE); strip.appendChild(playhead);

        // readout
        var readout = el('div', 'tt-readout');
        var startChip = el('div', 'tt-chip'); startChip.innerHTML = '<span class="tt-chip-k">' + (labels.start || 'Start') + '</span><b>0:00.0</b>';
        var endChip = el('div', 'tt-chip'); endChip.innerHTML = '<span class="tt-chip-k">' + (labels.end || 'End') + '</span><b>0:00.0</b>';
        var keepChip = el('div', 'tt-chip tt-keepchip'); keepChip.innerHTML = '<span class="tt-chip-k">' + (labels.keep || 'Keep') + '</span><b>0:00.0</b>';
        var setIn = el('button', 'tt-setbtn'); setIn.type = 'button'; setIn.innerHTML = (labels.setIn || 'Set start') + ' <kbd>[</kbd>';
        var setOut = el('button', 'tt-setbtn'); setOut.type = 'button'; setOut.innerHTML = (labels.setOut || 'Set end') + ' <kbd>]</kbd>';
        var sideL = el('div', 'tt-side'); sideL.appendChild(setIn); sideL.appendChild(startChip);
        var sideR = el('div', 'tt-side'); sideR.appendChild(endChip); sideR.appendChild(setOut);
        readout.appendChild(sideL); readout.appendChild(keepChip); readout.appendChild(sideR);

        // Одной строкой — как этим пользоваться. Ручки видно, но что они
        // делают и почему кнопки бывают выключены, само собой не читается.
        var hint = el('div', 'tt-hint');
        hint.textContent = labels.hint || '';
        if (!labels.hint) hint.style.display = 'none';

        root.appendChild(strip); root.appendChild(readout); root.appendChild(hint);
        container.appendChild(root);

        function pxOf(t) { var w = strip.clientWidth || 1; return (t / (dur || 1)) * w; }
        function tOf(px) { var w = strip.clientWidth || 1; return clamp(px / w * (dur || 1), 0, dur); }

        function renderKf() {
            kfLayer.innerHTML = '';
            if (!keyframes.length || keyframes.length > 400) return; // don't clutter on all-intra
            for (var i = 0; i < keyframes.length; i++) {
                if (keyframes[i] <= 0.01 || keyframes[i] >= dur - 0.01) continue;
                var tick = el('div', 'tt-kf'); tick.style.left = pxOf(keyframes[i]) + 'px'; kfLayer.appendChild(tick);
            }
        }
        function render() {
            var w = strip.clientWidth || 1;
            var xs = pxOf(startT), xe = pxOf(endT);
            dimL.style.width = xs + 'px';
            dimR.style.width = (w - xe) + 'px';
            keep.style.left = xs + 'px'; keep.style.width = Math.max(0, xe - xs) + 'px';
            // Ручка живёт ВНУТРИ полосы (у неё overflow:hidden). Слева тело
            // ручки стоит перед точкой реза, справа — после неё; на самом краю
            // зажим кладёт её поверх крайних пикселей оставляемого куска,
            // чтобы она осталась видимой и хватаемой.
            var hw = hS.offsetWidth || 16;
            var maxL = Math.max(0, w - hw);
            hS.style.left = clamp(xs - hw, 0, maxL) + 'px';
            hE.style.left = clamp(xe, 0, maxL) + 'px';
            pxWrite(playhead, 'left', pxOf(playT), 'px');
            renderKf();
            startChip.querySelector('b').textContent = fmt(startT);
            endChip.querySelector('b').textContent = fmt(endT);
            keepChip.querySelector('b').textContent = fmt(endT - startT);
            hS.setAttribute('aria-valuetext', fmt(startT));
            hE.setAttribute('aria-valuetext', fmt(endT));
            syncSetBtns();
        }

        /* Кнопки «Отметить начало/конец» ставят границу по КУРСОРУ
           ВОСПРОИЗВЕДЕНИЯ, поэтому их смысл целиком зависит от того, где он
           стоит. Выключаем ровно тогда, когда нажатие либо ничего не изменит,
           либо оставит огрызок короче MIN_BTN. */
        function syncSetBtns() {
            var canIn = playT > startT + 0.05 && playT < endT - MIN_BTN;
            var canOut = playT < endT - 0.05 && playT > startT + MIN_BTN;
            if (setIn.disabled === canIn) { setIn.disabled = !canIn; setIn.title = canIn ? '' : (labels.setHint || ''); }
            if (setOut.disabled === canOut) { setOut.disabled = !canOut; setOut.title = canOut ? '' : (labels.setHint || ''); }
        }
        function emitChange() { if (opts.onChange) try { opts.onChange(startT, endT); } catch (_) {} }

        // ── handle dragging ──
        function dragHandle(which, ev) {
            ev.preventDefault();
            var h = which === 's' ? hS : hE;
            h.classList.add('tt-drag');
            var rect = strip.getBoundingClientRect();
            // Где ВНУТРИ ручки её взяли. Без этого первый же pointerdown
            // швырял границу в точку курсора: ручка на краю нарисована со
            // смещением, и «просто нажать, не двигая» сдвигало рез.
            var anchorPx = pxOf(which === 's' ? startT : endT);
            var grabDx = (ev.clientX - rect.left) - anchorPx;
            function move(e) {
                var cx = (e.clientX != null ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0));
                var t = tOf(cx - rect.left - grabDx);
                // START snaps to a keyframe (lossless cuts begin only there);
                // END is free (copy drops trailing samples at any point).
                if (which === 's') startT = clamp(snapStart(t), 0, endT - MIN);
                else endT = clamp(t, startT + MIN, dur);
                render(); emitChange();
            }
            function up() {
                h.classList.remove('tt-drag');
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
                window.removeEventListener('pointercancel', up);
            }
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
            // Касание отменяется системным жестом — без этого слушатели текут
            // и следующий тап продолжал тащить ручку.
            window.addEventListener('pointercancel', up);
        }
        hS.addEventListener('pointerdown', function (e) { dragHandle('s', e); });
        hE.addEventListener('pointerdown', function (e) { dragHandle('e', e); });

        // keyboard nudge on handles
        function keyNudge(which, e) {
            var step = e.shiftKey ? 1 : (1 / 30); // frame-ish vs 1s
            var d = 0;
            if (e.key === 'ArrowLeft') d = -step; else if (e.key === 'ArrowRight') d = step;
            else if (e.key === 'Home') { if (which === 's') startT = 0; render(); emitChange(); e.preventDefault(); return; }
            else if (e.key === 'End') { if (which === 'e') endT = dur; render(); emitChange(); e.preventDefault(); return; }
            else return;
            if (which === 's') startT = clamp(startT + d, 0, endT - MIN);
            else endT = clamp(endT + d, startT + MIN, dur);
            render(); emitChange(); e.preventDefault();
        }
        hS.addEventListener('keydown', function (e) { keyNudge('s', e); });
        hE.addEventListener('keydown', function (e) { keyNudge('e', e); });

        // ── scrub (click/drag the strip anywhere not on a handle) ──
        function scrubAt(clientX) {
            var rect = strip.getBoundingClientRect();
            playT = tOf(clientX - rect.left);
            render();
            if (opts.onScrub) try { opts.onScrub(playT); } catch (_) {}
        }
        strip.addEventListener('pointerdown', function (e) {
            if (e.target === hS || e.target === hE) return;
            e.preventDefault();
            scrubAt(e.clientX);
            function move(ev) { scrubAt(ev.clientX); }
            function up() {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
                window.removeEventListener('pointercancel', up);
            }
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
            window.addEventListener('pointercancel', up);
        });

        function applySetIn() { startT = clamp(snapStart(playT), 0, endT - MIN); render(); emitChange(); }
        function applySetOut() { endT = clamp(playT, startT + MIN, dur); render(); emitChange(); }
        setIn.addEventListener('click', applySetIn);
        setOut.addEventListener('click', applySetOut);

        /* ⚠️ Кнопки НАПЕЧАТАНЫ с подсказками клавиш [ и ], а обработчика этих
           клавиш не было НИГДЕ — ни здесь, ни у тула. То есть компонент
           показывал горячую клавишу, которая ничего не делает: тот же класс,
           что и остальные жалобы этой правки («кнопки не работают»). Клавиши
           подчиняются ровно тому же условию, что и кнопки: выключённая кнопка
           не срабатывает и с клавиатуры. */
        function onKey(e) {
            if (destroyed || (window.__keyChord ? window.__keyChord(e) : (e.ctrlKey || e.metaKey))) return;
            var t = e.target;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
            if (!root.isConnected) return;
            // «[ ]» by the key's place: the Russian layout prints «х ъ» there.
            var ks = window.__keySign ? window.__keySign(e) : e.key;
            if (ks === '[') { if (setIn.disabled) return; e.preventDefault(); applySetIn(); }
            else if (ks === ']') { if (setOut.disabled) return; e.preventDefault(); applySetOut(); }
        }
        document.addEventListener('keydown', onKey);

        var ro = null;
        if (window.ResizeObserver) { ro = new ResizeObserver(function () { render(); }); ro.observe(strip); }

        // ── filmstrip thumbnails ──
        var thumbVideo = null, thumbURL = null;
        function buildStrip(fileOrUrl) {
            cells.innerHTML = '';
            var w = strip.clientWidth || 480;
            var N = clamp(Math.round(w / 88), 6, 20);
            var cellEls = [];
            for (var i = 0; i < N; i++) { var c = el('div', 'tt-cell tt-shimmer'); cells.appendChild(c); cellEls.push(c); }
            // hidden probe video seeked to N sample points
            var v = document.createElement('video');
            v.muted = true; v.playsInline = true; v.preload = 'auto'; v.crossOrigin = 'anonymous';
            v.style.cssText = 'position:absolute;left:-99999px;width:160px;height:90px';
            thumbURL = (typeof fileOrUrl === 'string') ? fileOrUrl : URL.createObjectURL(fileOrUrl);
            v.src = thumbURL; document.body.appendChild(v); thumbVideo = v;
            var cv = document.createElement('canvas');
            var idx = 0;
            function shotNext() {
                if (destroyed || idx >= N) { cleanupProbe(); return; }
                var t = ((idx + 0.5) / N) * (dur || v.duration || 1);
                var onSeeked = function () {
                    v.removeEventListener('seeked', onSeeked);
                    try {
                        var vw = v.videoWidth || 160, vh = v.videoHeight || 90;
                        cv.width = 160; cv.height = Math.round(160 * vh / vw);
                        cv.getContext('2d').drawImage(v, 0, 0, cv.width, cv.height);
                        var cell = cellEls[idx];
                        cell.style.backgroundImage = 'url(' + cv.toDataURL('image/jpeg', 0.6) + ')';
                        cell.classList.remove('tt-shimmer');
                    } catch (_) { cellEls[idx] && cellEls[idx].classList.remove('tt-shimmer'); }
                    idx++; shotNext();
                };
                v.addEventListener('seeked', onSeeked);
                try { v.currentTime = Math.min(t, (v.duration || dur) - 0.05); } catch (_) { idx++; shotNext(); }
            }
            var kick = function () { v.removeEventListener('loadeddata', kick); shotNext(); };
            if (v.readyState >= 2) shotNext(); else v.addEventListener('loadeddata', kick);
            // safety: if metadata never comes, drop the shimmer after 8s
            setTimeout(function () { cellEls.forEach(function (c) { c.classList.remove('tt-shimmer'); }); }, 8000);
        }
        function cleanupProbe() {
            if (thumbVideo) { try { thumbVideo.pause(); thumbVideo.removeAttribute('src'); thumbVideo.load(); thumbVideo.remove(); } catch (_) {} thumbVideo = null; }
            if (thumbURL && typeof thumbURL !== 'string' || (thumbURL && thumbURL.indexOf && thumbURL.indexOf('blob:') === 0)) { try { URL.revokeObjectURL(thumbURL); } catch (_) {} }
            thumbURL = null;
        }

        return {
            load: function (fileOrUrl, durationSec) {
                dur = durationSec > 0 ? durationSec : 0;
                startT = 0; endT = dur; playT = 0;
                render();
                buildStrip(fileOrUrl);
                return this;
            },
            setPlayhead: function (t) { playT = clamp(t || 0, 0, dur); pxWrite(playhead, 'left', pxOf(playT), 'px'); syncSetBtns(); },
            setKeyframes: function (arr) { keyframes = (arr || []).filter(function (t) { return t >= 0 && t <= dur; }); render(); },
            getRange: function () { return { start: startT, end: endT }; },
            // The clip's full length — NOT the range end. Telemetry once logged
            // `getRange().end` as «fullMs», so a trimmed end looked like a short
            // clip and a wrong cut was invisible next to it.
            getDuration: function () { return dur; },
            // Programmatic range change must reach the host too — the
            // download button and the drag pill both label themselves
            // from onChange, and a silent setRange left them saying
            // "full recording" while the UI showed a trim.
            setRange: function (s, e) { startT = clamp(snapStart(s), 0, dur); endT = clamp(e, startT + MIN, dur); render(); emitChange(); },
            isTrimmed: function () { return startT > 0.05 || endT < dur - 0.05; },
            root: root,
            destroy: function () {
                destroyed = true;
                document.removeEventListener('keydown', onKey);
                if (ro) try { ro.disconnect(); } catch (_) {}
                cleanupProbe();
                try { container.removeChild(root); } catch (_) {}
            }
        };
    };
})();
